import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Plus } from '@phosphor-icons/react';
import { Button, Modal } from 'app/components';
import { DraggableList, Question, QuestionBuilder } from './components';
import { ACTION_ITEMS } from './constants';
import { generateUniqueId } from './utils';
import './style';

const Step3 = (props) => {
  const {
    editmode,
    updateProgress,
    updateStep,
    subStep,
  } = props;

  const { eventData } = useSelector((state) => state.eventCreate);

  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState({ isOpen: false });

  const dispatch = useDispatch();

  useEffect(() => {
    if (subStep?.source === 'next') {
      if (subStep?.step === 1) updateProgress(100);
      else updateStep(4);
    } else {
      updateStep(2);
    }
  }, [subStep]);

  const onCloseQuestionModal = () => {
    if (selectedQuestion) setSelectedQuestion(null);
    setIsQuestionModalOpen(false);
  }

  const onAddQuestion = (questionConfig) => {
    // add to questions list
    const index = (eventData?.get_to_know_details || [])?.findIndex((item) => item?.id === questionConfig?.id);
    let updatedQuestions = [...(eventData?.get_to_know_details || [])];

    if (index > -1) {
      updatedQuestions[index] = questionConfig;
    } else updatedQuestions = updatedQuestions.concat({ ...questionConfig, id: generateUniqueId() });

    onChangeQuestions(updatedQuestions);
    onCloseQuestionModal();
  };

  const onCtaClick = (ctaType, id) => {
    if (ctaType === 'edit') {
      setSelectedQuestion(eventData?.get_to_know_details?.find((item) => item?.label === id));
      setIsQuestionModalOpen(true);
    } else if (ctaType === 'delete') {
      setShowDeleteConfirmationModal({
        id,
        isOpen: true,
      });
    }
  };

  const onMarkRequired = (id) => {
    const updatedQuestions = eventData?.get_to_know_details?.map((item) => {
      if (item?.id === id) return { ...item, isRequired: !item?.isRequired };
      return item;
    });
    onChangeQuestions(updatedQuestions);
  };

  const deleteSelectedQuestion = () => {
    const updatedQuestions = [...(eventData?.get_to_know_details || [])];
    const index = updatedQuestions?.findIndex((item) => item?.label === showDeleteConfirmationModal?.id);
    updatedQuestions.splice(index, 1);
    onChangeQuestions(updatedQuestions);
    setShowDeleteConfirmationModal({ isOpen: false });
  };

  const onChangeQuestions = (questions) => {
    dispatch({
      type: 'EVENT_CREATE:EVENT_DATA:SET',
      data: {
        ...eventData,
        get_to_know_details: questions,
      } });
  };

  const ticketsConfig = useMemo(() => {
    return eventData?.smp_detail_entity_set?.map((ticket) => ({ id: ticket.id, title: ticket.option_value_1 }));
  }, [eventData?.smp_detail_entity_set]);

  return (
    <div className="step-3-container flex flex-col flex-1 overflow-auto">
      {!editmode ?  
        <>
          <span className="heading-36">Get to know your audience</span>
          <div className="text-16 text-grey-70 mt-3">These will be asked when someone purchases tickets to your event.</div>
        </> :
        <div className='flex justify-between'>
          <h1 className='heading-24 flex items-center self-stretch'>Get to know your audience</h1>
          <Button
            type="tertiary"
            rounded={true}
            iconLeft={<Plus />}
            onClick={() => setIsQuestionModalOpen(true)}
          >
            Add question
          </Button>
        </div>
      }
      <div className='form-container-wrapper flex flex-col items-center flex-1 gap-6 mt-9 pr-4'>
        <div className="form-content w-full">
          <DraggableList
            list={eventData?.get_to_know_details || []}
            Component={Question}
            componentProps={{
              ctaList: ACTION_ITEMS,
              showRequiredToggle: true,
              onCtaClick,
              onToggle: onMarkRequired,
            }}
            onChange={onChangeQuestions}
          />
        </div>
        {!editmode && 
          <Button
            type="tertiary"
            rounded={true}
            iconLeft={<Plus />}
            onClick={() => setIsQuestionModalOpen(true)}
          >
            Add question
          </Button>
        } 
      </div>
      <QuestionBuilder
        onCloseModal={onCloseQuestionModal}
        isOpen={isQuestionModalOpen}
        onAddQuestion={onAddQuestion}
        selectedQuestion={selectedQuestion}
        ticketsConfig={ticketsConfig}
        list={eventData?.get_to_know_details || []}
      />
      <Modal
        className="delete-confirmation-modal"
        primaryText="Delete"
        size="regular"
        isOpen={showDeleteConfirmationModal?.isOpen}
        onClickPrimaryCta={deleteSelectedQuestion}
        onClickSecondaryCta={() => setShowDeleteConfirmationModal({ isOpen: false })}
        title="Are you sure you want to delete this question?"
      />
    </div>
  );
};

Step3.propTypes = {
  editmode: PropTypes.bool,
  updateProgress: PropTypes.func,
  updateStep: PropTypes.func,
  subStep: PropTypes.object,
}

Step3.defaultProps = {
  editmode: false,
  updateProgress: () => {},
  updateStep: () => {},
  subStep: {},
}
export default Step3;

